import { useEffect, useState } from 'react';
import config from 'src/config';
import type { StyleSpecification } from 'maplibre-gl';

export type Basemap = { style?: StyleSpecification; attribution: string };

/** No backdrop. The layers above still draw, over the background of the page. */
const NO_BASEMAP: Basemap = { attribution: '' };

/**
 * The backdrop for the MapLibre maps.
 *
 * A vector provider gives a whole style, which has to be read before anything can be
 * drawn over it. Until it arrives, and whenever it cannot be read, the layers above
 * draw over the background of the page rather than over somebody else's map.
 *
 * CAUTION: an unlicensed backdrop must never reach a customer. See ADR-003 for what
 * OpenFreeMap permits, and for why the Leaflet maps draw Google tiles and these do
 * not.
 */
const useMaplibreBasemap = (): Basemap => {
    const [baseStyle, setBaseStyle] = useState<StyleSpecification | null>(null);
    const styleUrl = config.maplibreBasemapStyleUrl;

    useEffect(() => {
        if (!styleUrl) return undefined;

        let current = true;
        fetch(styleUrl)
            .then(response => (response.ok ? response.json() : null))
            .then(style => {
                if (current && style) setBaseStyle(style as StyleSpecification);
            })
            // A map with no backdrop looks like a working map, so say which one is drawn.
            .catch(() => console.warn(`Map basemap: could not read the base style at ${styleUrl}.`));

        return () => {
            current = false;
        };
    }, [styleUrl]);

    return baseStyle ? { style: baseStyle, attribution: config.maplibreBasemapAttribution } : NO_BASEMAP;
};

export default useMaplibreBasemap;
